import React, { useState, useEffect } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    acceptImage,
    acceptDoc,
    messageError,
    messageToast,
    status200,
} from '../../utils/define';
import ReactSpinner from '../../components/reactSpinner/ReactSpinner';
import { ASSET_API, ASSETGROUP_API, DEPARTMENT_API, ASSETSTATUS_API, BaseGatewayImage, ASSET_ACCESSORY_API, MATERIALCATALOG_API } from '@app/utils/apiUrl';
import { useMutation, useQuery } from 'react-query';
import { Post, Get } from '@app/utils/httpProvider';
import moment from 'moment';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import InputFile from '@app/components/input-file/InputFile';
import { isValidUrl } from '@app/utils/helper';
import DocumentInput from '@app/components/document-input/DocumentInput';
import { BaseGateway } from '@app/utils/apiUrl';

const schema = yup.object().shape({
    assetCode: yup.string().required(messageError.emptyMessage),
    assetName: yup.string().required(messageError.emptyMessage),
    assetGroupId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn nhóm vật tư'),
    currentDepartmentId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn khoa'),
    assetStatusId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn trạng thái vật tư'),
    serialNumber: yup.string().nullable(),
    model: yup.string().nullable(),
    manufacturer: yup.string().nullable(),
    country: yup.string().nullable(),
    powerSpec: yup.string().nullable(),
    purchaseDate: yup.string().nullable(),
    purchaseValue: yup.number().nullable().min(0, 'Giá trị mua không được nhỏ hơn 0'),
});

const AssetAdd = () => {
    const history = useHistory();
    const location = useLocation();
    const cloneData = location.state?.cloneData || null;
    const cloneAccessories = location.state?.cloneAccessories || [];

    const [isLoading, setIsLoading] = useState(false);
    const [accessories, setAccessories] = useState(cloneAccessories);
    const [materialCatalog, setMaterialCatalog] = useState([]);
    
    // 1. Thêm state để quản lý lỗi cho danh sách phụ kiện (lưu theo index)
    const [accErrors, setAccErrors] = useState({});

    // Auto-fetch materialCatalog khi ở chế độ clone (phụ kiện đã được seed sẵn)
    useEffect(() => {
        if (cloneAccessories.length > 0 && materialCatalog.length === 0) {
            Get(MATERIALCATALOG_API.GET_ALL)
                .then(data => setMaterialCatalog(data || []))
                .catch(err => console.error('Lỗi tải danh sách phụ kiện:', err));
        }
    }, []);

    const assetGroups = useQuery(['getAllAssetGroupsForMapping'], () =>
        Get(ASSETGROUP_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const departments = useQuery(['getAllDepartmentsForMapping'], () =>
        Get(DEPARTMENT_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const assetStatuses = useQuery(['getAllAssetStatusesForMapping'], () =>
        Get(ASSETSTATUS_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const departmentOptions = departments?.data?.data?.map(item => ({ value: item.id, label: item.departmentName })) || [];
    const assetGroupOptions = assetGroups?.data?.data?.map(item => ({ value: item.id, label: item.assetGroupName })) || [];
    const assetStatusOptions = assetStatuses?.data?.data?.map(item => ({ value: item.id, label: item.statusName })) || [];

    const addFunc = useMutation((value) =>
        Post(ASSET_API.ADD_ITEM, null, value, false)
    );

    // 2. Cập nhật hàm validateAccessories để hiển thị lỗi inline
    const validateAccessories = () => {
        let isValid = true;
        const newAccErrors = {};

        accessories.forEach((item, index) => {
            const rowErrors = {};

            // Validation Tên phụ kiện
            if (!item.materialName?.trim()) {
                rowErrors.materialId = "Vui lòng chọn tên phụ kiện.";
                isValid = false;
            }

            // Validation Số lượng
            const qty = Number(item.quantity);
            if (!item.quantity) {
                rowErrors.quantity = "Vui lòng nhập số lượng.";
                isValid = false;
            } else if (isNaN(qty) || qty <= 0) {
                rowErrors.quantity = "Số lượng phải lớn hơn 0.";
                isValid = false;
            } else if (!Number.isInteger(qty)) {
                rowErrors.quantity = "Số lượng phải là số nguyên.";
                isValid = false;
            } else if (qty > 10000) {
                rowErrors.quantity = "Số lượng vượt quá giới hạn.";
                isValid = false;
            }

            if (Object.keys(rowErrors).length > 0) {
                newAccErrors[index] = rowErrors;
            }
        });

        setAccErrors(newAccErrors);

        // if (!isValid) {
        //     toast.error("Vui lòng kiểm tra lại thông tin phụ kiện!");
        // }

        return isValid;
    };

    const handleCreate = async (values) => {
        // Validate accessories before submitting the main asset form
        if (!validateAccessories()) return;

        const model = { ...values };

        // Convert input formats back to numbers
        model.assetGroupId = Number(model.assetGroupId);
        model.currentDepartmentId = Number(model.currentDepartmentId);
        model.assetStatusId = Number(model.assetStatusId);
        model.purchaseValue = model.purchaseValue ? Number(model.purchaseValue) : null;

        addFunc.mutate(model, {
            onSuccess: async (res) => {
                if (res.status < 200 || res.status >= 300) {
                    let errorMessage = messageToast.messageToastCreateError;
                    try {
                        const errData = await res.json();
                        if (errData?.message) {
                            errorMessage = errData.message;
                        } else if (errData?.errors) {
                            // Xử lý lỗi từ FluentValidation hoặc ModelState
                            errorMessage = Object.values(errData.errors).flat().join(', ');
                        }
                    } catch (e) {
                        console.error("Error parsing response JSON:", e);
                    }
                    toast.error(errorMessage);
                } else {
                    setIsLoading(true);
                    const data = await res.json();

                    const assetId = data?.id || data?.assetId;

                    for (const item of accessories) {
                        // ===== create asset accessory =====
                        const accessoryPayload = {
                            assetId: assetId,
                            materialId: Number(item.materialId) || null,
                            quantity: Number(item.quantity),
                            accessoryStatus: item.accessoryStatus,
                            note: item.note,
                            isDeleted: false
                        };

                        await Post(`${ASSET_ACCESSORY_API.ADD_ITEM}`, null, accessoryPayload);
                    }
                    toast.success(messageToast.messageToastCreateSuccess);
                    history.goBack();
                }
            },
            onError: (err) => {
                let errorMessage = 'Đã có lỗi xảy ra, vui lòng thử lại !';
                if (err?.message) {
                    errorMessage = err.message;
                } else if (err?.errors) {
                    errorMessage = Object.values(err.errors).flat().join(', ');
                }
                toast.error(errorMessage);
            }
        });
    };

    const handleAddRow = async () => {
        // Gọi API lấy danh sách phụ kiện nếu state chưa có dữ liệu
        if (materialCatalog.length === 0) {
            try {
                const data = await Get(`${MATERIALCATALOG_API.GET_ALL}`);
                setMaterialCatalog(data || []);
            } catch (error) {
                console.error("Lỗi lấy danh sách phụ kiện:", error);
                toast.error("Không thể tải danh sách phụ kiện!");
            }
        }
        setAccessories(prev => [
            ...prev,
            {
                materialId: null,
                materialCode: "",
                materialName: "",
                specification: "",
                unit: "",
                quantity: "",
                accessoryStatus: "Hoạt động",
                note: ""
            }
        ]);
    };

    const handleChangeAccessory = (index, field, value) => {
        const newList = [...accessories];
        newList[index][field] = value;
        setAccessories(newList);

        // 3. Xóa lỗi của trường đang nhập (real-time clear error)
        if (accErrors[index] && accErrors[index][field]) {
            setAccErrors(prev => ({
                ...prev,
                [index]: {
                    ...prev[index],
                    [field]: null
                }
            }));
        }
    };

    const handleRemoveRow = (index) => {
        const newList = accessories.filter((_, i) => i !== index);
        setAccessories(newList);

        // 4. Khi xóa 1 dòng, cần dịch chuyển index của mảng lỗi để tránh bị sai dòng báo lỗi
        setAccErrors(prev => {
            const newErrors = {};
            let newIdx = 0;
            for (let i = 0; i < accessories.length; i++) {
                if (i === index) continue;
                if (prev[i]) {
                    newErrors[newIdx] = prev[i];
                }
                newIdx++;
            }
            return newErrors;
        });
    };

    return (
        <Card className="shadow-sm">
            <Card.Header className="bg-white d-flex align-items-center py-3">
                <h5 className="card-title fw-bold text-primary mb-0">
                    <i className="fas fa-plus-circle mr-2" />
                    Thêm tài sản mới
                </h5>
            </Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleCreate(values)}
                    initialValues={{
                        assetCode: '',
                        assetName: cloneData?.assetName || '',
                        assetGroupId: cloneData?.assetGroupId || 0,
                        currentDepartmentId: cloneData?.currentDepartmentId
                            || departments?.data?.data?.find(x => (x.departmentCode || x.code || '').trim().toUpperCase() === 'CNTT')?.id
                            || 0,
                        assetStatusId: assetStatuses?.data?.data?.find(x => x.statusCode === 'NEW')?.id || 0,
                        serialNumber: cloneData?.serialNumber || '',
                        model: cloneData?.model || '',
                        manufacturer: cloneData?.manufacturer || '',
                        country: cloneData?.country || '',
                        powerSpec: cloneData?.powerSpec || '',
                        purchaseDate: cloneData?.purchaseDate || '',
                        purchaseValue: cloneData?.purchaseValue || 0,
                        isDisposed: false,
                        imagePath: '',
                        imageThumbnail: '',
                        documentLink: []
                    }}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        values,
                        errors,
                        setFieldValue
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Row>
                                <Col md={12}>
                                    <h6 className="mb-3 text-primary">Thông tin chung</h6>
                                    <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                            {
                                                label: 'Mã tài sản',
                                                name: 'assetCode',
                                                value: values.assetCode,
                                                onChange: handleChange,
                                                isInvalid: errors.assetCode,
                                                error: errors.assetCode
                                            },
                                            {
                                                label: 'Tên tài sản',
                                                name: 'assetName',
                                                value: values.assetName,
                                                onChange: handleChange,
                                                isInvalid: errors.assetName,
                                                error: errors.assetName
                                            }
                                        ]}
                                    />
                                    <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                            {
                                                label: 'Nhóm TS',
                                                name: 'assetGroupId',
                                                component: (
                                                    <Select
                                                        options={assetGroupOptions}
                                                        value={assetGroupOptions.find(opt => opt.value === values.assetGroupId) || null}
                                                        onChange={(selectedOption) => setFieldValue('assetGroupId', selectedOption ? selectedOption.value : 0)}
                                                        placeholder="-- Chọn nhóm tài sản --"
                                                        isClearable={true}
                                                        className={errors.assetGroupId ? 'is-invalid' : ''}
                                                    />
                                                ),
                                                error: errors.assetGroupId
                                            },
                                            {
                                                label: 'Phòng ban',
                                                name: 'currentDepartmentId',
                                                component: (
                                                    <Select
                                                        options={departmentOptions}
                                                        value={departmentOptions.find(opt => opt.value === values.currentDepartmentId) || null}
                                                        onChange={(selectedOption) => setFieldValue('currentDepartmentId', selectedOption ? selectedOption.value : 0)}
                                                        placeholder="-- Chọn phòng ban --"
                                                        isClearable={true}
                                                        className={errors.currentDepartmentId ? 'is-invalid' : ''}
                                                    />
                                                ),
                                                error: errors.currentDepartmentId
                                            }
                                        ]}
                                    />
                                    <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                            {
                                                label: 'Số Serial',
                                                name: 'serialNumber',
                                                value: values.serialNumber,
                                                onChange: handleChange,
                                                isInvalid: errors.serialNumber,
                                                error: errors.serialNumber
                                            },
                                            {
                                                label: 'Model / Ký hiệu',
                                                name: 'model',
                                                value: values.model,
                                                onChange: handleChange,
                                                isInvalid: errors.model,
                                                error: errors.model
                                            }
                                        ]}
                                    />
                                    <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                            {
                                                label: 'Nhà sản xuất',
                                                name: 'manufacturer',
                                                value: values.manufacturer,
                                                onChange: handleChange,
                                                isInvalid: errors.manufacturer,
                                                error: errors.manufacturer
                                            },
                                            {
                                                label: 'Xuất xứ',
                                                name: 'country',
                                                value: values.country,
                                                onChange: handleChange,
                                                isInvalid: errors.country,
                                                error: errors.country
                                            }
                                        ]}
                                    />
                                    <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                            {
                                                label: 'Công suất',
                                                name: 'powerSpec',
                                                value: values.powerSpec,
                                                onChange: handleChange,
                                                isInvalid: errors.powerSpec,
                                                error: errors.powerSpec
                                            },
                                            {
                                                label: "Ngày mua",
                                                name: 'purchaseDate',
                                                component: (
                                                    <DatePicker
                                                        wrapperClassName="w-100"
                                                        className={`form-control ${errors.purchaseDate ? 'is-invalid' : ''}`}
                                                        selected={values.purchaseDate ? moment(values.purchaseDate, 'YYYY-MM-DD').toDate() : null}
                                                        onChange={(date) => setFieldValue('purchaseDate', date ? moment(date).format('YYYY-MM-DD') : '')}
                                                        dateFormat="dd/MM/yyyy"
                                                        isClearable
                                                        placeholderText="dd/MM/yyyy"
                                                    />
                                                ),
                                                error: errors.purchaseDate
                                            }
                                        ]}
                                    />
                                    <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                            {
                                                label: 'Giá trị mua (VND)',
                                                name: 'purchaseValue',
                                                type: 'number',
                                                min: 0,
                                                value: values.purchaseValue,
                                                onChange: handleChange,
                                                isInvalid: errors.purchaseValue,
                                                error: errors.purchaseValue
                                            },
                                            null // Empty right slot
                                        ]}
                                    />
                                    <HorizontalFieldsRow
                                                    labelCol={4}
                                                    inputCol={8}
                                                    fields={[
                                            {
                                                label: 'Trạng thái',
                                                name: 'assetStatusId',
                                                component: (
                                                    <Select
                                                        options={assetStatusOptions}
                                                        value={assetStatusOptions.find(opt => opt.value === values.assetStatusId) || null}
                                                        onChange={(selectedOption) => setFieldValue('assetStatusId', selectedOption ? selectedOption.value : 0)}
                                                        placeholder="-- Chọn trạng thái --"
                                                        isClearable={true}
                                                        isDisabled={true}
                                                        className={errors.assetStatusId ? 'is-invalid' : ''}
                                                    />
                                                ),
                                                error: errors.assetStatusId
                                            },
                                            {
                                                label: 'Tài sản đã thanh lý',
                                                name: 'isDisposed',
                                                component: (
                                                    <Form.Check
                                                        type="checkbox"
                                                        label="Đánh dấu tài sản đã được thanh lý"
                                                        name="isDisposed"
                                                        checked={values.isDisposed}
                                                        onChange={handleChange}
                                                    />
                                                )
                                            }
                                        ]}
                                    />
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group as={Row} className="mb-3">
                                                <Form.Label className="col-sm-3 col-form-label">
                                                    Ảnh tài sản
                                                </Form.Label>
                                                <Col sm="9">
                                                    <InputFile
                                                        enableReinitialize={true}
                                                        values={(values.imageThumbnail && values.imageThumbnail !== 'null') ? [values.imageThumbnail] : ((values.imagePath && values.imagePath !== 'null') ? [values.imagePath] : [])}
                                                        name="imageThumbnail"
                                                        accept={acceptImage}
                                                        onChange={(files) => {
                                                            if (files.length > 0) {
                                                                setFieldValue('imagePath', files[0].filePath || files[0].url, true);
                                                                setFieldValue('imageThumbnail', files[0].thumbPath || files[0].url, true);
                                                            } else {
                                                                setFieldValue('imagePath', '', true);
                                                                setFieldValue('imageThumbnail', '', true);
                                                            }
                                                        }}
                                                    />
                                                </Col>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group as={Row} className="mb-3">
                                                <Form.Label className="col-sm-3 col-form-label">
                                                    Tài liệu kèm theo
                                                </Form.Label>
                                                <Col sm="9">
                                                    <InputFile
                                                        multiple
                                                        isDocument={true}
                                                        name="documentLink"
                                                        accept={acceptDoc}
                                                        values={values.documentLink || []}
                                                        onChange={(files) => {
                                                            setFieldValue(
                                                                'documentLink',
                                                                files.map(f => f.filePath || f.url).filter(Boolean),
                                                                true
                                                            );
                                                        }}
                                                    />

                                                    <div
                                                        style={{
                                                            display: 'block',
                                                            color: '#dc3545'
                                                        }}
                                                        className="invalid-feedback"
                                                    >
                                                        {errors.documentLink}
                                                    </div>
                                                </Col>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                            <h6 className="mb-3 text-primary mt-4">Danh sách phụ kiện</h6>

                            <div className="table-responsive">
                                <table className="table table-bordered table-sm mb-0">
                                    <thead>
                                        <tr className="text-dark fw-bold text-uppercase" style={{ fontSize: '12px', background: '#f8f9fa' }}>
                                            <th className="text-center py-2" style={{ width: '50px' }}>STT</th>
                                            <th className="py-2">Tên phụ kiện <span className="text-danger">*</span></th>
                                            <th className="py-2" style={{ width: '110px' }}>Số lượng <span className="text-danger">*</span></th>
                                            <th className="py-2" style={{ width: '140px' }}>Trạng thái</th>
                                            <th className="py-2">Ghi chú</th>
                                            <th className="text-center py-2" style={{ width: '80px' }}>Thao tác</th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        {accessories.map((item, index) => (
                                            <tr key={index}>
                                                <td className="text-center align-middle">{index + 1}</td>

                                                <td className="align-top">
                                                    {/* 5. Gắn class is-invalid và hiển thị lỗi Tên phụ kiện */}
                                                    <select
                                                        className={`form-control ${accErrors[index]?.materialId ? 'is-invalid' : ''}`}
                                                        value={item.materialName}
                                                        onChange={(e) => {
                                                            const selectedName = e.target.value;
                                                            const selectedMaterial = materialCatalog.find(m => m.materialName === selectedName);

                                                            const newList = [...accessories];
                                                            newList[index].materialName = selectedName;

                                                            // Tự động điền các thông tin khác của phụ kiện khi được chọn
                                                            if (selectedMaterial) {
                                                                newList[index].materialId = selectedMaterial.id;
                                                                newList[index].materialCode = selectedMaterial.materialCode || "";
                                                                newList[index].unit = selectedMaterial.unit || "";
                                                                newList[index].specification = selectedMaterial.specification || "";
                                                            } else {
                                                                newList[index].materialId = null;
                                                                newList[index].materialCode = "";
                                                                newList[index].unit = "";
                                                                newList[index].specification = "";
                                                            }

                                                            setAccessories(newList);

                                                            // Xóa lỗi materialId khi người dùng chọn phụ kiện
                                                            if (accErrors[index]?.materialId) {
                                                                setAccErrors(prev => ({
                                                                    ...prev,
                                                                    [index]: {
                                                                        ...prev[index],
                                                                        materialId: null
                                                                    }
                                                                }));
                                                            }
                                                        }}
                                                    >
                                                        <option value="">-- Chọn phụ kiện --</option>
                                                        {materialCatalog.map((mat, idx) => (
                                                            <option key={idx} value={mat.materialName}>
                                                                {mat.materialName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {accErrors[index]?.materialId && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{accErrors[index].materialId}</div>}
                                                </td>

                                                <td className="align-top">
                                                    {/* 6. Gắn class is-invalid và hiển thị lỗi Số lượng */}
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        className={`form-control ${accErrors[index]?.quantity ? 'is-invalid' : ''}`}
                                                        value={item.quantity}
                                                        onChange={(e) =>
                                                            handleChangeAccessory(index, "quantity", e.target.value)
                                                        }
                                                    />
                                                    {accErrors[index]?.quantity && <div className="text-danger mt-1" style={{ fontSize: '0.875em' }}>{accErrors[index].quantity}</div>}
                                                </td>

                                                <td className="align-top">
                                                    <select
                                                        className="form-control"
                                                        value={item.accessoryStatus}
                                                        onChange={(e) =>
                                                            handleChangeAccessory(index, "accessoryStatus", e.target.value)
                                                        }
                                                    >
                                                        <option value="Hoạt động">Hoạt động</option>
                                                        <option value="Không hoạt động">Không hoạt động</option>
                                                    </select>
                                                </td>

                                                <td className="align-top">
                                                    <input
                                                        className="form-control"
                                                        value={item.note}
                                                        onChange={(e) =>
                                                            handleChangeAccessory(index, "note", e.target.value)
                                                        }
                                                    />
                                                </td>

                                                <td className="text-center align-middle">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleRemoveRow(index)}
                                                        title="Xóa"
                                                    >
                                                        <i className="far fa-trash-alt" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}



                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-3 mb-2">
                                <button type="button" className="btn btn-outline-success btn-sm" onClick={handleAddRow}>
                                    <i className="fas fa-plus mr-1" /> Thêm phụ kiện
                                </button>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-end">
                                <Button
                                    type="submit"
                                    theme="primary"
                                    disabled={isLoading}
                                    className="mr-2"
                                >
                                    <i className="fas fa-save mr-1" /> Lưu tài sản
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => history.goBack()}
                                    theme="secondary"
                                >
                                    <i className="fas fa-times mr-1" /> Hủy bỏ
                                </Button>
                            </div>
                        </Form>
                    )}
                </Formik>
                <ReactSpinner loadings={isLoading} />
            </Card.Body>
        </Card>
    );
};

export default AssetAdd;